import { Link } from "react-router-dom";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/common/PageHero";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CTASection } from "@/components/common/CTASection";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Monitor, Layers, Globe, Smartphone, Check, Minus, Cpu, LineChart } from "lucide-react";

const PLATFORMS = [
  { icon: Monitor, name: "MetaTrader 5", text: "A multi-asset platform offering advanced charting, a wide range of order types, an economic calendar and algorithmic trading via MQL5." },
  { icon: Layers, name: "MetaTrader 4", text: "The long-established platform trusted globally for forex, featuring expert advisors, custom indicators and a familiar interface." },
  { icon: Globe, name: "Web Trading", text: "Trade directly from your browser with no download required — full charting and order management from any device." },
  { icon: Smartphone, name: "Mobile Trading", text: "Native apps for iOS and Android let you monitor markets, manage positions and receive alerts on the move." },
];

const ROWS = [
  ["Advanced charting", true, true, true, true],
  ["Algorithmic trading", true, true, false, false],
  ["No download required", false, false, true, true],
  ["Push notifications", true, true, false, true],
  ["Economic calendar", true, false, true, true],
];

export default function TradingPlatforms() {
  const { L } = useLang();
  const P = L.pages.tradingPlatforms;
  usePageSeo("tradingPlatforms");
  return (
    <>
      <PageHero
        overline={P.overline}
        title={P.title}
        description={P.desc}
        breadcrumbs={[{ label: L.common.home, to: "/" }, { label: L.nav.sub.platforms[0] }]}
      />

      <section className="py-20 lg:py-28">
        <div className="container-tight grid gap-6 md:grid-cols-2">
          {PLATFORMS.map((p) => (
            <div key={p.name} className="flex gap-5 rounded-xl border border-hair bg-white p-6 shadow-sm">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-navy text-gold"><p.icon className="h-6 w-6" strokeWidth={1.5} /></span>
              <div>
                <h3 className="font-heading text-lg font-semibold text-ink">{p.name}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-subtle">{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-canvas py-20 lg:py-28">
        <div className="container-tight">
          <SectionHeading overline="Comparison" title="Desktop vs Web vs Mobile" description="A general comparison of platform capabilities. Exact features depend on the brokerage partner." />
          <div className="mt-10 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
            <Table>
              <TableHeader>
                <TableRow className="bg-navy hover:bg-navy">
                  <TableHead className="text-white">Capability</TableHead>
                  <TableHead className="text-center text-white">MT5</TableHead>
                  <TableHead className="text-center text-white">MT4</TableHead>
                  <TableHead className="text-center text-white">Web</TableHead>
                  <TableHead className="text-center text-white">Mobile</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ROWS.map((r, i) => (
                  <TableRow key={r[0]} className={i % 2 ? "bg-canvas" : ""}>
                    <TableCell className="font-medium text-ink">{r[0]}</TableCell>
                    {r.slice(1).map((v, j) => (
                      <TableCell key={j} className="text-center">
                        {v ? <Check className="mx-auto h-4 w-4 text-success" /> : <Minus className="mx-auto h-4 w-4 text-subtle/50" />}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="container-tight grid gap-6 md:grid-cols-2">
          <div className="rounded-xl border border-hair bg-white p-8 shadow-sm">
            <Cpu className="h-6 w-6 text-finblue" strokeWidth={1.5} />
            <h3 className="mt-4 font-heading text-xl font-semibold text-ink">Algorithmic Trading</h3>
            <p className="mt-2 text-sm leading-relaxed text-subtle">Automate strategies using expert advisors and scripts on MetaTrader, subject to the broker's terms.</p>
          </div>
          <div className="rounded-xl border border-hair bg-white p-8 shadow-sm">
            <LineChart className="h-6 w-6 text-finblue" strokeWidth={1.5} />
            <h3 className="mt-4 font-heading text-xl font-semibold text-ink">Technical Analysis Tools</h3>
            <p className="mt-2 text-sm leading-relaxed text-subtle">Access indicators, drawing tools and multiple timeframes for in-depth market analysis.</p>
          </div>
        </div>
        <div className="container-tight mt-10"><Button asChild className="bg-finblue hover:bg-finblueDark"><Link to="/broker-partners">{P.cta}</Link></Button></div>
      </section>

      <CTASection />
    </>
  );
}
